import { useState, useCallback, useRef } from 'react';
import type { FieldRendererProps, FieldDisplayProps } from '../../config/types';
import { Combobox } from '@/components/ui/Combobox';
import { Text } from '@/components/ui/Text';
import { cn } from '@/lib/cn';
import { useContextStore } from '@/lib/session/context-store';
import { useAssignableAgencies } from '@/lib/onboarding/use-assignable-agencies';
import type { OnboardUnder } from '@/lib/onboarding/resolve-manager-scope';

const SELF: OnboardUnder = { kind: 'self' };
const DEBOUNCE_MS = 300;
const MIN_SEARCH_LEN = 2;

/**
 * Custom field — "Onboard under" (client-onboarding-polish §4). Super-Admin-only:
 * Self (direct client) vs an Agency, with a searchable agency picker. Value is an
 * {@link OnboardUnder} object; non-SA personas never get this field, but it stays
 * defensive and renders nothing.
 */
export function OnboardUnderField({ value, onChange, error }: FieldRendererProps) {
  const persona = useContextStore((s) => s.active?.persona);
  const v = (value as OnboardUnder | undefined) ?? SELF;

  const [searchQ, setSearchQ] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearch = useCallback((q: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setSearchQ(q), DEBOUNCE_MS);
  }, []);

  const activeQ = searchQ.length >= MIN_SEARCH_LEN ? searchQ : undefined;
  const agencies = useAssignableAgencies(activeQ, persona === 'SA' && v.kind === 'agency');

  if (persona !== 'SA') return null;

  const options = (agencies.data ?? []).map((a) => ({
    value: a.tenantId,
    label: a.displayName,
    skipFilter: true as const,
  }));

  return (
    <div className="space-y-2">
      <div role="radiogroup" aria-label="Onboard under" className="flex gap-2">
        {(['self', 'agency'] as const).map((kind) => {
          const active = v.kind === kind;
          return (
            <button
              key={kind}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(kind === 'self' ? SELF : { kind: 'agency', agencyTenantId: '' })}
              className={cn(
                'flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring',
                active
                  ? 'border-primary bg-primary-subtle text-primary'
                  : 'border-border bg-surface-elevated text-text-muted hover:text-text',
              )}
            >
              {kind === 'self' ? 'Self (direct client)' : 'An agency'}
            </button>
          );
        })}
      </div>

      {v.kind === 'agency' && (
        <div className="space-y-1">
          <Combobox
            options={options}
            value={v.agencyTenantId || undefined}
            onValueChange={(agencyTenantId) => onChange({ kind: 'agency', agencyTenantId })}
            placeholder={agencies.isLoading ? 'Loading agencies…' : 'Select an agency…'}
            // White form input, consistent with the other fields (override the
            // Combobox's default grey filter background).
            className="bg-surface-elevated"
            onSearch={handleSearch}
          />
          {agencies.isError && <Text variant="label-sm" color="danger">Couldn't load agencies.</Text>}
          {!agencies.isLoading && !agencies.isError && options.length === 0 && (
            <Text variant="label-sm" color="subtle">No agencies available.</Text>
          )}
        </div>
      )}

      {error && <Text variant="label-sm" color="danger">{error}</Text>}
    </div>
  );
}

/** Review-step display for the onboard-under selection. */
export function OnboardUnderDisplay({ value }: FieldDisplayProps) {
  const v = (value as OnboardUnder | undefined) ?? SELF;
  return (
    <Text variant="body-sm" as="span">
      {v.kind === 'self' ? 'Self (direct client)' : 'An agency'}
    </Text>
  );
}
